import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, ChevronDown, Sparkles } from 'lucide-react';
import { Button } from './ui/button';

export const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Close mobile menu when route changes
  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location.pathname]);

  const navLinks = [
    { to: '/', label: 'Home' },
    { to: '/product', label: 'Product' },
    { to: '/about', label: 'About Us' },
    { to: '/integrations', label: 'Integrations' },
    { to: '/faq', label: 'FAQ' },
    { to: '/careers', label: 'Careers' },
    { to: '/contact', label: 'Contact' }
  ];

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        isScrolled 
          ? 'bg-white/98 backdrop-blur-2xl shadow-2xl border-b border-gray-200/50' 
          : 'bg-white/80 backdrop-blur-md'
      }`}
    >
      {/* Top accent line */}
      <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-teal-500 via-blue-500 to-purple-500"></div>
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-20">
          {/* Logo - Enhanced */}
          <Link to="/" className="flex items-center space-x-3 group relative">
            {/* Glow effect on hover */}
            <div className="absolute -inset-2 bg-gradient-to-r from-teal-500/20 to-blue-500/20 rounded-2xl opacity-0 group-hover:opacity-100 blur-xl transition-opacity duration-500"></div>
            
            <div className="relative w-12 h-12 bg-gradient-to-br from-teal-500 via-teal-600 to-blue-600 rounded-xl flex items-center justify-center shadow-lg group-hover:shadow-2xl group-hover:scale-110 transition-all duration-300">
              <span className="text-white font-bold text-xl">F</span>
              {/* Shine effect */}
              <div className="absolute inset-0 bg-gradient-to-tr from-white/0 via-white/30 to-white/0 rounded-xl opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
            </div>
            
            <div className="relative">
              <div className="text-2xl font-black bg-gradient-to-r from-gray-900 via-gray-800 to-gray-900 bg-clip-text text-transparent group-hover:from-teal-600 group-hover:via-blue-600 group-hover:to-teal-600 transition-all duration-300">
                Finsage
              </div>
              <div className="text-xs text-gray-500 font-medium tracking-wide">Mare Labs LLP</div>
            </div>
          </Link>

          {/* Desktop Navigation - Enhanced */}
          <nav className="hidden lg:flex items-center space-x-1">
            {navLinks.map((link) => {
              const isActive = location.pathname === link.to;
              return (
                <Link
                  key={link.to}
                  to={link.to}
                  className="relative group px-4 py-2"
                >
                  <span className={`text-sm font-semibold transition-colors duration-300 ${
                    isActive
                      ? 'text-teal-600'
                      : 'text-gray-700 group-hover:text-teal-600'
                  }`}>
                    {link.label}
                  </span>
                  
                  {/* Active indicator */}
                  {isActive && (
                    <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-1.5 h-1.5 bg-teal-600 rounded-full"></div>
                  )}
                  
                  {/* Hover effect */}
                  <div className="absolute inset-0 bg-teal-50 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-300 -z-10"></div>
                </Link>
              );
            })}
            
            {/* CTA Button - Enhanced */}
            <Link to="/demo" className="ml-4">
              <Button className="relative group bg-gradient-to-r from-teal-600 to-blue-600 hover:from-teal-700 hover:to-blue-700 text-white font-bold px-6 py-2.5 rounded-full shadow-lg hover:shadow-xl transition-all duration-300 overflow-hidden">
                <span className="relative z-10 flex items-center gap-2">
                  <Sparkles className="w-4 h-4" />
                  Book a Demo
                </span>
                {/* Shine animation */}
                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-700"></div>
              </Button>
            </Link>
          </nav>

          {/* Mobile Menu Button - Enhanced */}
          <button
            className="lg:hidden relative p-2.5 rounded-xl hover:bg-gray-100 transition-all duration-300 group"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            aria-label="Toggle menu"
          >
            <div className="relative w-6 h-6">
              {isMobileMenuOpen ? (
                <X className="w-6 h-6 text-gray-900 transition-transform duration-300 rotate-90" />
              ) : (
                <Menu className="w-6 h-6 text-gray-900 transition-transform duration-300 group-hover:scale-110" />
              )}
            </div>
          </button>
        </div>
      </div>

      {/* Mobile Menu - Enhanced */}
      <div
        className={`lg:hidden overflow-hidden transition-all duration-500 ease-in-out ${
          isMobileMenuOpen ? 'max-h-screen opacity-100' : 'max-h-0 opacity-0'
        }`}
      >
        <div className="bg-gradient-to-b from-white to-gray-50 border-t border-gray-200 shadow-2xl">
          <nav className="px-4 py-6 space-y-2 max-w-md mx-auto">
            {navLinks.map((link, index) => {
              const isActive = location.pathname === link.to;
              return (
                <Link
                  key={link.to}
                  to={link.to}
                  className={`block px-4 py-3 rounded-xl font-semibold transition-all duration-300 transform ${
                    isActive
                      ? 'bg-gradient-to-r from-teal-500 to-blue-500 text-white shadow-lg scale-105'
                      : 'text-gray-700 hover:bg-teal-50 hover:text-teal-600 hover:translate-x-2'
                  }`}
                  style={{ 
                    animationDelay: `${index * 50}ms`,
                    animation: isMobileMenuOpen ? 'slideIn 0.3s ease-out forwards' : 'none'
                  }}
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  <div className="flex items-center justify-between">
                    <span>{link.label}</span>
                    {isActive && <div className="w-2 h-2 bg-white rounded-full"></div>}
                  </div>
                </Link>
              );
            })}
            
            {/* Mobile CTA */}
            <Link 
              to="/demo" 
              onClick={() => setIsMobileMenuOpen(false)}
              className="block pt-4"
            >
              <Button className="w-full bg-gradient-to-r from-teal-600 to-blue-600 hover:from-teal-700 hover:to-blue-700 text-white font-bold py-4 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300">
                <span className="flex items-center justify-center gap-2">
                  <Sparkles className="w-5 h-5" />
                  Book a Demo
                </span>
              </Button>
            </Link>
          </nav>
        </div>
      </div>

      <style jsx>{`
        @keyframes slideIn {
          from {
            opacity: 0;
            transform: translateX(-20px);
          }
          to {
            opacity: 1;
            transform: translateX(0);
          }
        }
      `}</style>
    </header>
  );
};